import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
} from "@mui/material";

// ──────────────────────────────────────────────────────────────────────
// BulkDeleteConfirmDialog
//
// AISC-grade bulk-delete confirmation. Shows the user EXACTLY which tasks
// they're about to delete, requires typing "DELETE" to enable the destructive
// button (Option 3 of CR-2026-AISC-TASK-BULK-DELETE), and surfaces the
// soft-delete + recovery posture so the user knows it's recoverable for
// 30 days via the admin restore tool.
//
// Per AISC 303 §4 — destructive actions on records require explicit two-step
// confirmation. The type-DELETE pattern is the cheapest defense against
// accidental select-all + click-delete that wipes a job's worth of fab tasks.
// ──────────────────────────────────────────────────────────────────────

const REQUIRED_CONFIRM_TEXT = "DELETE";
const PMK_PREVIEW_LIMIT = 10;

export default function BulkDeleteConfirmDialog({
  open,
  onClose,
  selectedTasks,
  onConfirm,
  isLoading,
}) {
  const [confirmText, setConfirmText] = useState("");

  // Reset confirmation text whenever the dialog opens — prevents a stale
  // "DELETE" string from a previous session enabling the button.
  useEffect(() => {
    if (open) setConfirmText("");
  }, [open]);

  const taskCount = selectedTasks?.length || 0;
  const previewTasks = (selectedTasks || []).slice(0, PMK_PREVIEW_LIMIT);
  const remainingCount = Math.max(0, taskCount - PMK_PREVIEW_LIMIT);
  const isConfirmEnabled =
    confirmText === REQUIRED_CONFIRM_TEXT && !isLoading && taskCount > 0;

  const handleConfirm = () => {
    if (!isConfirmEnabled) return;
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="bulk-delete-dialog-title"
    >
      <DialogTitle id="bulk-delete-dialog-title" sx={{ color: "error.main" }}>
        Delete {taskCount} Task{taskCount === 1 ? "" : "s"}?
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This will soft-delete the selected task{taskCount === 1 ? "" : "s"}.
          Deletion is recoverable for 30 days via the admin restore tool.
          Approved drawings remain attached and untouched.
        </Alert>

        {previewTasks.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              You are about to delete:
            </Typography>
            <List
              dense
              sx={{
                bgcolor: "grey.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.200",
                maxHeight: 240,
                overflowY: "auto",
              }}
            >
              {previewTasks.map((task) => (
                <ListItem key={task.id} disableGutters sx={{ px: 2 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        PMK: {task.pmk_number || "(no PMK)"}
                        {" — "}
                        {task.heatNo || "(no heat)"}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {task.description || "(no description)"}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
              {remainingCount > 0 && (
                <ListItem disableGutters sx={{ px: 2 }}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: "italic", color: "text.secondary" }}
                      >
                        ...and {remainingCount} more
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Box>
        )}

        <Typography variant="body2" sx={{ mb: 1 }}>
          Type <strong>{REQUIRED_CONFIRM_TEXT}</strong> to confirm:
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={REQUIRED_CONFIRM_TEXT}
          disabled={isLoading}
          inputProps={{
            "aria-label": "Type DELETE to confirm bulk deletion",
            autoComplete: "off",
            autoCorrect: "off",
            autoCapitalize: "off",
            spellCheck: "false",
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!isConfirmEnabled}
          color="error"
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? "Deleting..." : `Delete ${taskCount} Task${taskCount === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BulkDeleteConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedTasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      pmk_number: PropTypes.string,
      heatNo: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

BulkDeleteConfirmDialog.defaultProps = {
  selectedTasks: [],
  isLoading: false,
};
